import { NIR, CHECKISH } from './grid-util.js';
export class GridMouse {
    /** @internal */ emitCellClick(e) {
        if (!this.opts.onCellClick)
            return;
        const h = this.hit(e), row = h ? this.disp[h.vi] : -1;
        if (h && row >= 0 && this.cols[h.ci])
            this.opts.onCellClick({ row, col: this.cols[h.ci].id, grid: this });
    }
    /** @internal */ onMouseDown(e) {
        if (e.button !== 0)
            return;
        const h = this.hit(e);
        if (!h) {
            setTimeout(() => this.ime.focus({ preventScroll: true }));
            return;
        } // 스크롤바·빈 영역
        e.preventDefault(); // 포커스를 ime 에 유지
        this.ime.focus({ preventScroll: true });
        if (h.vi === NIR) {
            this.onNirMouseDown(h.ci);
            return;
        }
        if (h.vi >= this.disp.length)
            return;
        if (this.editing) {
            if (h.vi === this.editing.vi && h.ci === this.editing.ci)
                return;
            if (!this.commitEdit())
                return;
        }
        const row = this.disp[h.vi];
        if (row < 0) {
            this.setFocus(h.vi, h.ci, false);
            this.toggleGroupAt(h.vi);
            return;
        }
        const c = this.cols[h.ci];
        if (c?.special === 'drag') {
            this.startRowDrag(row);
            return;
        }
        if (c?.special === 'detail') {
            this.setFocus(h.vi, h.ci, false);
            this.toggleDetail(row);
            return;
        }
        if (c && !c.special && this.opts.editable && this.editorOf(c, row) === 'button' && this.canEditCol(c, row)) {
            const cel = e.target.closest('.og-c');
            if (cel && e.clientX > cel.getBoundingClientRect().right - 20) {
                this.setFocus(h.vi, h.ci, false);
                void this.openButtonEdit(row, c);
                return;
            }
        }
        if (this.opts.rowResize && (c?.special === 'rownum' || c?.special === 'indicator' || c?.special === 'state')) {
            const cel = e.target.closest('.og-c');
            if (cel && e.clientY > cel.getBoundingClientRect().bottom - 5) {
                this.startRowResize(row, h.vi, e);
                return;
            }
        }
        if (c?.special === 'indicator') {
            this.selectRowRange(e.shiftKey ? this.anchor.vi : h.vi, h.vi);
            return;
        }
        if (c?.def.onCellClick && e.target.closest('.og-cbtn,.og-link')) {
            this.setFocus(h.vi, h.ci, false);
            c.def.onCellClick(row, c.id, this);
            return;
        }
        if (this.opts.tree && c && c.id === this.treeLabelCol() && this.treeKids.has(row)) {
            const cellEl = e.target.closest('.og-c');
            const x = e.clientX - cellEl.getBoundingClientRect().left;
            if (x < parseFloat(cellEl.style.paddingLeft || '6') + 14) {
                this.setFocus(h.vi, h.ci, false);
                this.toggleNode(row);
                return;
            }
        }
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey && c && !c.special) {
            // Ctrl+클릭·끌기: 지금 범위를 남겨 두고 새 범위 시작 (떨어진 여러 범위 선택)
            this.extraRanges.push(this.range());
            this.keepExtra = true;
            try {
                this.setFocus(h.vi, h.ci, false);
            }
            finally {
                this.keepExtra = false;
            }
            this.ver++;
            this.dragging = true;
            return;
        }
        this.setFocus(h.vi, h.ci, e.shiftKey);
        if (c?.special === 'check') {
            this.toggleCheckRow(row);
            return;
        }
        if (c && CHECKISH.has(this.editorOf(c, row)) && this.canEditCol(c, row)) {
            this.toggleCheckCell(c, row);
            return;
        }
        this.dragging = true;
    }
    /** @internal */ onMouseMove(e) {
        if (!this.dragging || !(e.buttons & 1))
            return;
        const h = this.hit(e);
        if (h && (h.vi !== this.focus.vi || h.ci !== this.focus.ci))
            this.setFocus(h.vi, h.ci, true);
    }
    /** @internal */ onDblClick(e) {
        const h = this.hit(e);
        if (!h)
            return;
        if (h.vi === NIR) {
            if (this.focus.vi === NIR)
                this.beginEdit('full');
            return;
        }
        const row = this.disp[h.vi];
        if (row < 0)
            return;
        this.opts.onCellDblClick?.({ row, col: this.cols[h.ci]?.id, grid: this });
        this.beginEdit('full');
    }
    /** @internal */ onTouchStart(e) {
        if (this.tch)
            clearTimeout(this.tch.timer);
        if (e.touches.length !== 1) {
            this.tch = null;
            return;
        }
        const q = e.touches[0], target = e.target;
        const zone = this.touchZone(target);
        if (zone === 'drag') {
            this.tch = null;
            this.touchDrag(e, target, false);
            return;
        } // ⋮⋮ 손잡이: 바로 행 끌기 (다른 그리드로도)
        const t = this.tch = { x: q.clientX, y: q.clientY, t: Date.now(), moved: false, long: false, timer: 0 };
        t.timer = setTimeout(() => {
            if (this.tch !== t || t.moved)
                return;
            t.long = true;
            this.noCtxUntil = Date.now() + 1000; // 브라우저가 따로 보내는 contextmenu 는 무시
            this.onCellContext({ target, clientX: t.x, clientY: t.y, preventDefault() { } });
        }, 550);
        if (zone === 'range')
            this.touchDrag(e, target, true); // 포커스 셀에서 시작한 끌기 = 범위 선택 (가만히 누르면 위 길게 누름 메뉴)
    }
    /** 터치 시작 자리: 'drag' = 행 끌기 손잡이, 'range' = 지금 포커스 셀(끌면 범위 선택), null = 보통(탭·스크롤) */
    /** @internal */ touchZone(target) {
        if (this.editing || target.closest('input,textarea,select,button,a,.og-cbtn,.og-link'))
            return null;
        const h = this.hit({ target });
        if (!h || !target.closest('.og-c'))
            return null;
        const row = this.disp[h.vi], c = this.cols[h.ci];
        if (row == null || row < 0 || !c)
            return null;
        if (c.special === 'drag')
            return 'drag';
        if (!c.special && h.vi === this.focus.vi && h.ci === this.focus.ci && !CHECKISH.has(this.editorOf(c, row)))
            return 'range';
        return null;
    }
    /**
     * 터치 끌기 → 마우스 이벤트로 바꿔 보냄: 기존 마우스 끌기 처리(행 이동·다른 그리드로 끌어 놓기·범위 선택·채우기·컬럼 너비)를 그대로 쓴다.
     * 손가락 아래 요소(elementFromPoint)를 대상으로 mousemove 를 보내므로 hit()·놓을 자리 계산이 마우스와 같다.
     * click = 움직이지 않고 떼면 click 도 보냄 (터치 시작을 막아 브라우저가 만들던 클릭이 없으므로)
     */
    /** @internal */ touchDrag(e, el, click) {
        e.preventDefault(); // 스크롤·브라우저의 마우스 흉내 이벤트 막음
        const q = e.touches[0], id = q.identifier, x0 = q.clientX, y0 = q.clientY, t0 = this.tch;
        let x = x0, y = y0, moved = false;
        const fire = (type, t) => t.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true, composed: true, clientX: x, clientY: y, screenX: x, screenY: y, button: 0, buttons: type === 'mouseup' || type === 'click' ? 0 : 1, view: window }));
        const at = () => document.elementFromPoint(x, y) ?? document.body;
        const mine = (ev) => Array.prototype.find.call(ev.changedTouches, (p) => p.identifier === id);
        const mv = (ev) => {
            const p = mine(ev);
            if (!p)
                return;
            ev.preventDefault();
            x = p.clientX;
            y = p.clientY;
            if (Math.hypot(x - x0, y - y0) > 6)
                moved = true;
            if (t0?.long)
                return; // 길게 눌러 메뉴가 열렸으면 끌지 않음
            fire('mousemove', at());
        };
        const end = (ev) => {
            if (!mine(ev))
                return;
            tt.removeEventListener('touchmove', mv);
            tt.removeEventListener('touchend', end);
            tt.removeEventListener('touchcancel', end);
            fire('mouseup', at());
            if (click && !moved && ev.type === 'touchend' && !t0?.long)
                fire('click', el);
        };
        // 터치 이벤트는 처음 닿은 요소로만 온다 — 그 요소가 다시 그려져 문서에서 빠져도(머리글 너비 조절 등) 받도록 그 요소에 직접 단다
        const tt = e.target;
        tt.addEventListener('touchmove', mv, { passive: false });
        tt.addEventListener('touchend', end);
        tt.addEventListener('touchcancel', end);
        fire('mousedown', el);
    }
    /** @internal */ onTouchEnd(e) {
        const t = this.tch;
        this.tch = null;
        if (!t)
            return;
        clearTimeout(t.timer);
        if (t.long) {
            e.preventDefault();
            return;
        } // 메뉴를 연 뒤 따라오는 마우스 클릭이 메뉴를 닫지 않게
        if (t.moved || Date.now() - t.t > 500)
            return;
        const now = Date.now(), lt = this.lastTap;
        if (lt && now - lt.t < 350 && Math.hypot(t.x - lt.x, t.y - lt.y) < 24) {
            // 두 번 탭 → 그 셀 편집
            this.lastTap = null;
            e.preventDefault();
            const h = this.hit({ target: e.target });
            if (!h || this.disp[h.vi] < 0)
                return;
            if (this.focus.vi !== h.vi || this.focus.ci !== h.ci)
                this.setFocus(h.vi, h.ci, false);
            this.opts.onCellDblClick?.({ row: this.disp[h.vi], col: this.cols[h.ci]?.id, grid: this });
            this.beginEdit('full');
            return;
        }
        this.lastTap = { t: now, x: t.x, y: t.y };
    }
}
